import React from 'react'
import { Field } from 'react-final-form'
import PropTypes from 'prop-types'

import styled, { css } from 'styled-components'
import { themeGet } from '@styled-system/theme-get'

import Flex from 'Components/UI/Flex'
import Text from 'Components/UI/Text'
import { DIV_SHOULD_FORWARD_PROP } from 'Config/shouldForwardProp'

import FieldError from '../FieldError'

const disabledCss = ({ disabled }) =>
  disabled &&
  css`
    textarea {
      background-color: ${themeGet('colors.seashell')};
    }
  `

const blueprintCss = ({ blueprint }) =>
  blueprint &&
  css`
    textarea {
      height: 120px;
      background-color: ${themeGet('colors.white')};
      border: 1px solid ${themeGet('colors.calculator.border')};
      border-radius: 2px;
      font-weight: 400;
      font-size: 14px;
      padding: 14px 14px;

      ::placeholder {
        color: ${themeGet('colors.calculator.textSecondary')};
      }
    }
  `

const shouldForwardProp = prop => {
  return ![...DIV_SHOULD_FORWARD_PROP, 'blueprint', 'disabled'].includes(
    String(prop),
  )
}

const Container = styled(Flex).withConfig({
  shouldForwardProp,
})`
  flex-direction: column;
  position: relative;
  width: ${({ w }) => (w ? `${w}px` : '100%')};

  textarea {
    margin: 0;
    // width: 100%;
    height: 100%;
    border-radius: 8px;
    padding: 15px;
    background-color: ${themeGet('colors.white')};
    outline: none;
    resize: none;
    appearance: none;

    ::placeholder {
      color: ${themeGet('colors.font.placeholder')};
    }

    ${props =>
      props.error &&
      css`
        border-color: ${themeGet('colors.error')};
      `};

    background-color: ${themeGet('colors.white')};
    border: 1px solid ${themeGet('colors.alto')};
    font-size: 18px;
    color: ${themeGet('colors.font.primary')};

    :focus {
      border: 1px solid ${themeGet('colors.primary')};
    }
  }

  ${disabledCss};
  ${blueprintCss};
`

const Label = styled(Text).attrs({ as: 'label' })`
  font-size: 18px;
  color: ${themeGet('colors.font.placeholder')};
  padding-bottom: 5px;
`

const renderField = ({
  input,
  meta,
  label,
  placeholder,
  autoComplete,
  className,
  disabled,
  noError,
  ...rest
}) => {
  const { touched, error, submitError, dirtySinceLastSubmit } = meta
  const hasError = touched && (error || (submitError && !dirtySinceLastSubmit))
  return (
    <Container
      className={className}
      disabled={disabled ? 1 : 0}
      error={hasError ? 'true' : undefined}
      {...rest}
    >
      {label && <Label>{label}</Label>}
      <textarea
        autoComplete={autoComplete}
        disabled={disabled}
        {...input}
        placeholder={placeholder}
      />
      {!noError && hasError && <FieldError meta={meta} />}
    </Container>
  )
}

renderField.defaultProps = {
  noError: false,
}

renderField.propTypes = {
  autoComplete: PropTypes.oneOf(['on', 'off']).isRequired,
  className: PropTypes.string.isRequired,
  disabled: PropTypes.bool.isRequired,
  input: PropTypes.object.isRequired,
  label: PropTypes.string.isRequired,
  meta: PropTypes.object.isRequired,
  noError: PropTypes.bool,
  placeholder: PropTypes.string.isRequired,
}

const Textarea = ({
  autoComplete = 'on',
  label = '',
  name,
  placeholder = '',
  className = '',
  disabled = false,
  ...rest
}) => (
  <Field
    autoComplete={autoComplete}
    className={className}
    disabled={disabled}
    label={label}
    name={name}
    placeholder={placeholder}
    render={renderField}
    {...rest}
  />
)

Textarea.propTypes = {
  autoComplete: PropTypes.oneOf(['on', 'off']),
  className: PropTypes.string,
  disabled: PropTypes.bool,
  label: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  name: PropTypes.string.isRequired,
  placeholder: PropTypes.string,
}

export default Textarea
